import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { 
    Car, 
    CreditCard, 
    Hammer, 
    Wallet, 
    Plane, 
    Heart, 
    GraduationCap, 
    Armchair, 
    Briefcase, 
    MoreHorizontal,
    Info,
    CheckCircle2
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { LoanApplicationForm } from '@/components/LoanApplicationForm';

const loanCategories = [
    { id: 'vehicles', icon: Car, color: 'bg-rose-500' },
    { id: 'debts', icon: CreditCard, color: 'bg-amber-500' },
    { id: 'renovations', icon: Hammer, color: 'bg-violet-500' },
    { id: 'liquidity', icon: Wallet, color: 'bg-emerald-500' },
    { id: 'travel', icon: Plane, color: 'bg-sky-500' },
    { id: 'health', icon: Heart, color: 'bg-pink-500' },
    { id: 'studies', icon: GraduationCap, color: 'bg-indigo-500' },
    { id: 'furniture', icon: Armchair, color: 'bg-teal-500' },
    { id: 'business', icon: Briefcase, color: 'bg-slate-600' },
    { id: 'others', icon: MoreHorizontal, color: 'bg-gray-500' },
];

const gradients = {
    'bg-rose-500': 'from-rose-500 to-pink-600',
    'bg-amber-500': 'from-amber-500 to-orange-600',
    'bg-violet-500': 'from-violet-500 to-purple-600',
    'bg-emerald-500': 'from-emerald-500 to-teal-600',
    'bg-sky-500': 'from-sky-500 to-cyan-600',
    'bg-pink-500': 'from-pink-500 to-rose-600',
    'bg-indigo-500': 'from-indigo-500 to-violet-600',
    'bg-teal-500': 'from-teal-500 to-emerald-600',
    'bg-slate-600': 'from-slate-600 to-gray-700',
    'bg-gray-500': 'from-gray-500 to-slate-600',
};

export const LoanSimulator = ({ onContactClick }) => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const handleCategorySelect = (categoryId) => {
        setSelectedCategory(categoryId);
        setIsFormOpen(true);
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setTimeout(() => setSelectedCategory(null), 300);
    };

    return (
        <section id="simulator" className="py-20 lg:py-28 bg-muted/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="text-center mb-12">
                    <p className="text-primary font-bold text-xl sm:text-2xl lg:text-3xl mb-6 max-w-4xl mx-auto leading-relaxed">
                        {t('simulator.tagline')}
                    </p>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                        {t('simulator.title')}
                    </h2>
                    <div className="line-accent mx-auto mb-6" />
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <span>{t('simulator.subtitle')}</span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Info className="w-4 h-4 text-muted-foreground/50 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-xs">{t('simulator.tooltipInfo')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>

                <Card className="card-glass p-6 sm:p-10 max-w-5xl mx-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                        {loanCategories.map((category) => {
                            const Icon = category.icon;
                            const isSelected = selectedCategory === category.id;
                            const gradient = gradients[category.color] || 'from-gray-500 to-slate-600';
                            
                            return (
                                <button
                                    key={category.id}
                                    onClick={() => handleCategorySelect(category.id)}
                                    className="group relative"
                                >
                                    <div className={`
                                        relative overflow-hidden
                                        flex flex-col items-center justify-center
                                        p-6 min-h-[150px]
                                        rounded-3xl
                                        transition-all duration-300 ease-out
                                        ${isSelected 
                                            ? `bg-gradient-to-br ${gradient} shadow-2xl scale-105` 
                                            : 'bg-white shadow-lg hover:shadow-2xl hover:scale-105 hover:-translate-y-2'
                                        }
                                    `}>
                                        <div className={`
                                            absolute -top-6 -right-6 w-24 h-24 rounded-full
                                            transition-all duration-500
                                            ${isSelected 
                                                ? 'bg-white/10' 
                                                : `bg-gradient-to-br ${gradient} opacity-10 group-hover:opacity-30 group-hover:scale-150`
                                            }
                                        `} />
                                        <div className={`
                                            absolute -bottom-4 -left-4 w-16 h-16 rounded-full
                                            transition-all duration-500 delay-100
                                            ${isSelected 
                                                ? 'bg-white/10' 
                                                : `bg-gradient-to-br ${gradient} opacity-5 group-hover:opacity-20 group-hover:scale-125`
                                            }
                                        `} />
                                        
                                        <div className={`
                                            relative z-10
                                            w-16 h-16 mb-4
                                            rounded-2xl
                                            flex items-center justify-center
                                            transition-all duration-300
                                            ${isSelected 
                                                ? 'bg-white/20 backdrop-blur-sm' 
                                                : `bg-gradient-to-br ${gradient} shadow-lg group-hover:shadow-xl group-hover:scale-110`
                                            }
                                        `}>
                                            <Icon className="w-8 h-8 text-white" />
                                        </div>
                                        
                                        <span className={`
                                            relative z-10
                                            text-sm font-bold tracking-wide
                                            transition-colors duration-300
                                            ${isSelected 
                                                ? 'text-white' 
                                                : 'text-gray-700 group-hover:text-gray-900'
                                            }
                                        `}>
                                            {t(`simulator.categories.${category.id}`)}
                                        </span>
                                        
                                        {isSelected && (
                                            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/30 flex items-center justify-center">
                                                <CheckCircle2 className="w-4 h-4 text-white" />
                                            </div>
                                        )}
                                    </div>
                                    
                                    {isSelected && (
                                        <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-4 bg-gradient-to-r ${gradient} blur-xl opacity-60 rounded-full`} />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </Card>
            </div>

            {/* Loan Application Form Modal */}
            <LoanApplicationForm 
                isOpen={isFormOpen} 
                onClose={handleFormClose} 
                category={selectedCategory} 
            />
        </section>
    );
};
